import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, X, Sparkles, MapPin, Users, Trophy } from "lucide-react";
import { Crest, TenantThemer } from "../../components/shell";
import { FloodlightLoader } from "../../components/FloodlightLoader";

/**
 * Public matchday "What's available" page.
 *
 * Live at /{slug}/spots — toggled on/off by the club admin from Settings.
 * No prices. Enquiry-led. Renders fully in the club's brand colours.
 */
export default function MatchdayPage() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [presetInterest, setPresetInterest] = useState("");

    useEffect(() => {
        const base = process.env.REACT_APP_BACKEND_URL;
        axios
            .get(`${base}/api/public/tenants/${slug}/spots`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.status === 404 ? "Not currently open." : "Couldn't load. Please try again."));
    }, [slug]);

    if (err) {
        return (
            <div className="min-h-screen bg-[var(--sb-page)] text-white flex items-center justify-center p-12">
                <div className="border hairline p-10 max-w-md text-center">
                    <h1 className="font-sport text-3xl uppercase mb-3">Sponsorship enquiries aren't open right now.</h1>
                    <p className="text-sm text-zinc-400">Get in touch with the club directly — they'd still love to hear from you.</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[var(--sb-page)] flex items-center justify-center">
                <FloodlightLoader label="Lighting up the pitch" />
            </div>
        );
    }

    const { tenant, inventory, stats, packages } = data;
    const primary = tenant.brand?.primary || "#F59E0B";
    const fg = tenant.brand?.primary_foreground || "#000000";

    const openEnquiry = (interest = "") => { setPresetInterest(interest); setEnquiryOpen(true); };

    return (
        <div data-testid="matchday-page" className="min-h-screen bg-[var(--sb-page)] text-white relative overflow-hidden">
            <TenantThemer tenant={tenant} />

            {/* Background washes */}
            <div aria-hidden className="absolute inset-0" style={{
                background: `
                    radial-gradient(ellipse 60% 35% at 50% 0%, ${primary}26 0%, transparent 55%),
                    radial-gradient(ellipse 50% 40% at 0% 100%, ${primary}1a 0%, transparent 60%)
                `,
            }} />
            <div aria-hidden className="absolute inset-0 editorial-grid opacity-40" />

            <div className="relative z-10">
                {/* Header */}
                <header className="px-6 md:px-12 pt-8 flex items-center justify-between" data-testid="matchday-header">
                    <div className="flex items-center gap-3">
                        <Crest tenant={tenant} size={46} />
                        <div className="leading-tight">
                            <div className="font-display font-bold text-base tracking-tight">{tenant.name}</div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{tenant.season} · {tenant.league || ""}</div>
                        </div>
                    </div>
                    <div className="hidden md:block text-[10px] uppercase tracking-ultra-wide text-zinc-500">Open for sponsorship</div>
                </header>

                {/* Hero */}
                <section className="px-6 md:px-12 pt-20 md:pt-28 pb-16 max-w-6xl mx-auto" data-testid="matchday-hero">
                    <div className="inline-flex items-center gap-2 border hairline px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-300 mb-8">
                        <span className="kpi-pip animate-pulse" style={{ background: primary }} />
                        Available right now
                    </div>
                    <h1 className="font-sport uppercase text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] text-balance" style={{ letterSpacing: "-0.02em" }}>
                        See what's <span style={{ color: primary }}>open</span><br />at {tenant.short_name || tenant.name}.
                    </h1>
                    {tenant.matchday_page_intro && (
                        <p className="font-editorial italic text-zinc-300 mt-8 text-lg md:text-xl max-w-2xl leading-relaxed">"{tenant.matchday_page_intro}"</p>
                    )}
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                        <div className="flex items-baseline gap-2">
                            <span className="font-sport text-6xl md:text-7xl" style={{ color: primary }}>{stats.available}</span>
                            <span className="text-zinc-400 text-base">of {stats.total} sponsorship spots open today</span>
                        </div>
                    </div>
                    <button onClick={() => openEnquiry()} data-testid="matchday-cta-enquire"
                        className="mt-10 inline-flex items-center gap-2 px-7 py-4 font-semibold transition-all hover:brightness-110"
                        style={{ background: primary, color: fg }}>
                        I'm interested <ArrowRight size={16} strokeWidth={2.4} />
                    </button>
                </section>

                {/* Inventory by category */}
                <section className="px-6 md:px-12 py-16 md:py-24 max-w-6xl mx-auto" data-testid="matchday-inventory">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-4">What's available</div>
                    <h2 className="font-sport uppercase text-3xl md:text-5xl mb-12">Spots, by area.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
                        {inventory.map((cat) => (
                            <button
                                key={cat.category}
                                onClick={() => openEnquiry(cat.label)}
                                className="bg-[var(--sb-page)] p-7 text-left hover:bg-white/[0.03] transition-colors group"
                                data-testid={`matchday-cat-${cat.category}`}
                            >
                                <div className="flex items-start justify-between mb-5">
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{cat.label}</div>
                                    <span className="inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em]"
                                        style={{ borderColor: cat.available > 0 ? `${primary}66` : "rgba(82,82,91,0.4)", color: cat.available > 0 ? primary : "#71717a" }}>
                                        <span className="kpi-pip" style={{ background: cat.available > 0 ? primary : "#52525b" }} />
                                        {cat.available > 0 ? `${cat.available} open` : "Sold out"}
                                    </span>
                                </div>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="font-sport text-4xl md:text-5xl" style={{ color: cat.available > 0 ? primary : "#71717a" }}>{cat.available}</span>
                                    <span className="text-zinc-500 text-sm">of {cat.total}</span>
                                </div>
                                <div className="mt-4 h-1 bg-white/5">
                                    <div className="h-full transition-all" style={{ width: `${(cat.taken / cat.total) * 100}%`, background: `${primary}55` }} />
                                </div>
                                <div className="mt-6 text-xs text-zinc-500 inline-flex items-center gap-1 group-hover:text-white transition-colors">
                                    Enquire about {cat.label.toLowerCase()} <ArrowRight size={11} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
                                </div>
                            </button>
                        ))}
                    </div>
                </section>

                {/* Packages */}
                {packages.length > 0 && (
                    <section className="px-6 md:px-12 py-16 md:py-24 max-w-6xl mx-auto border-t hairline" data-testid="matchday-packages">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-4">Sponsor packages</div>
                        <h2 className="font-sport uppercase text-3xl md:text-5xl mb-3">Ways to back the club.</h2>
                        <p className="text-zinc-400 text-base max-w-2xl mb-12">Every business is different. We'll build a package that fits.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {packages.map((p) => (
                                <button key={p.name} onClick={() => openEnquiry(p.name)}
                                    className="border hairline p-6 text-left hover:border-white/30 transition-colors group" data-testid={`matchday-pkg-${p.name}`}>
                                    {p.tier && (
                                        <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: primary }}>{p.tier}</div>
                                    )}
                                    <div className="font-display font-bold text-lg tracking-tight">{p.name}</div>
                                    {p.description && (
                                        <p className="text-zinc-400 text-sm mt-2 leading-relaxed line-clamp-3">{p.description}</p>
                                    )}
                                    <div className="mt-5 text-xs text-zinc-500 inline-flex items-center gap-1 group-hover:text-white transition-colors">
                                        Enquire <ArrowRight size={11} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* Final CTA strip */}
                <section className="px-6 md:px-12 py-24 md:py-32 text-center" data-testid="matchday-final-cta">
                    <Sparkles size={28} className="mx-auto mb-6" style={{ color: primary }} strokeWidth={1.6} />
                    <h3 className="font-sport uppercase text-4xl md:text-6xl leading-[1] text-balance">
                        Back the <span style={{ color: primary }}>{tenant.short_name || tenant.name}</span>.<br />
                        Get seen by every fan who shows up.
                    </h3>
                    <p className="text-zinc-400 text-base md:text-lg mt-6 max-w-2xl mx-auto">Tell us a bit about your business and we'll come back with a tailored proposal.</p>
                    <button onClick={() => openEnquiry()} data-testid="matchday-final-enquire"
                        className="mt-10 inline-flex items-center gap-2 px-8 py-4 font-semibold transition-all hover:brightness-110"
                        style={{ background: primary, color: fg }}>
                        Send an enquiry <ArrowRight size={16} strokeWidth={2.4} />
                    </button>
                </section>

                <footer className="border-t hairline px-6 md:px-12 py-8 text-[11px] text-zinc-600 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <span className="font-editorial italic">{tenant.name} sponsor showcase</span>
                    <span>powered by <span className="text-zinc-400">FLOODLIGHT</span></span>
                </footer>
            </div>

            {enquiryOpen && (
                <EnquiryModal
                    slug={slug}
                    tenant={tenant}
                    presetInterest={presetInterest}
                    onClose={() => setEnquiryOpen(false)}
                />
            )}
        </div>
    );
}

const EnquiryModal = ({ slug, tenant, presetInterest, onClose }) => {
    const [draft, setDraft] = useState({ name: "", company: "", email: "", phone: "", interest: presetInterest, notes: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");
    const primary = tenant.brand?.primary || "#F59E0B";
    const fg = tenant.brand?.primary_foreground || "#000000";

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }, [onClose]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            await axios.post(`${base}/api/public/tenants/${slug}/enquiry`, draft);
            setDone(true);
        } catch (e2) {
            setErr(e2.response?.data?.detail || "Couldn't send. Please try again.");
        } finally { setBusy(false); }
    };

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center px-4 py-10 overflow-y-auto"
            data-testid="enquiry-modal"
        >
            <div className="w-full max-w-lg bg-[#0a0a0a] border hairline relative p-8 md:p-10">
                <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2" aria-label="Close" data-testid="enquiry-close">
                    <X size={18} />
                </button>
                {done ? (
                    <div className="text-center py-6" data-testid="enquiry-success">
                        <div className="w-14 h-14 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: `${primary}22` }}>
                            <Check size={26} style={{ color: primary }} strokeWidth={2.4} />
                        </div>
                        <h3 className="font-sport text-3xl uppercase mb-2">Thanks.</h3>
                        <p className="text-zinc-400 text-base max-w-sm mx-auto">The {tenant.short_name || tenant.name} commercial team will be in touch within one working day.</p>
                        <button onClick={onClose} className="mt-8 inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all" style={{ background: primary, color: fg }}>
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <div className="text-[10px] uppercase tracking-ultra-wide mb-2" style={{ color: primary }}>{tenant.short_name || tenant.name} · enquiry</div>
                        <h3 className="font-sport text-2xl md:text-3xl uppercase tracking-tight mb-2">Tell us about your business.</h3>
                        <p className="text-zinc-400 text-sm mb-6">Takes 30 seconds. We'll come back with a tailored proposal.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                            <EnquiryField label="Your name *">
                                <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Jane Smith"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none"
                                    data-testid="enquiry-name" />
                            </EnquiryField>
                            <EnquiryField label="Company">
                                <input value={draft.company} onChange={(e) => setDraft({ ...draft, company: e.target.value })} placeholder="Smith & Sons Ltd"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none"
                                    data-testid="enquiry-company" />
                            </EnquiryField>
                            <EnquiryField label="Email *">
                                <input required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="jane@yourbusiness.co.uk"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none"
                                    data-testid="enquiry-email" />
                            </EnquiryField>
                            <EnquiryField label="Phone">
                                <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="07700 900000"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none"
                                    data-testid="enquiry-phone" />
                            </EnquiryField>
                            <EnquiryField label="Interested in" className="md:col-span-2">
                                <input value={draft.interest} onChange={(e) => setDraft({ ...draft, interest: e.target.value })} placeholder="Pitch-side boards"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none"
                                    data-testid="enquiry-interest" />
                            </EnquiryField>
                            <EnquiryField label="Anything else we should know?" className="md:col-span-2">
                                <textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Budget range, season focus, audience…"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none resize-none"
                                    data-testid="enquiry-notes" />
                            </EnquiryField>
                        </div>
                        {err && <div className="mt-4 border border-rose-500/40 text-rose-300 text-xs px-3 py-2.5">{err}</div>}
                        <button type="submit" disabled={busy || !draft.name || !draft.email}
                            className="w-full mt-6 inline-flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-semibold transition-all hover:brightness-110 disabled:opacity-50"
                            style={{ background: primary, color: fg }}
                            data-testid="enquiry-submit">
                            {busy ? "Sending…" : <>Send enquiry <ArrowRight size={14} strokeWidth={2.4} /></>}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

const EnquiryField = ({ label, children, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="block text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">{label}</span>
        {children}
    </label>
);
